import { ServerEnvironment, ServerSettingsEnvironment } from './server-settings'
import { Language, SiteSelectorUrls, UserSettings } from './user-settings'
import { WebTogglesWithOverrides } from './web-toggles'
import { AuthConfig } from '@dustin-web/auth-js/dist/types/types'

const MOBILE_TYPES = ['mobile', 'tablet']

export type BaseLayoutInputPageConfig = {
  backgroundColor?: 'gray' | 'white'
}

export type BaseLayoutInputConfig = {
  showHeader?: boolean
  useDefaultBaseLayout?: boolean
  page?: BaseLayoutInputPageConfig
}

export type RequestServerContext = {
  site: Site
  isProxied: boolean
  isDevelopment: boolean
  correlationId: string
  clientIpAddress: string
  serverEnv: ServerEnvironment
  serverSettingsEnv: ServerSettingsEnvironment
  anonymousUserIdentifier: string
  esalesSessionKey: string
  host?: string
  siteUrl: string
  siteBaseUrl?: string
  deviceType?: string | null
  punchOutTicket?: string | null
  sessionUserIdentifier?: string | null
  percentageValueFromClientIp?: number
  userAgent?: string | null
}

export type RequestServerAppContext = RequestServerContext &
  ReturnType<typeof buildDerivedSiteContext>

export type CookieServerContext = {
  [key: string]: string
}

export type MicrositeSettings = {
  authConfig: AuthConfig & {
    siteType: string
    sourcingCompany: string
  }
  authServerConfig: AuthConfig
  graphqlConfig: {
    dustinHeaders: { [key: string]: string }
    graphqlServerUri: string
    graphqlUri: string
  }
  siteName: string
  isBusiness: boolean
}

export type ExpressLikeRequestContext = {
  header?: (name: string) => string
  headers: import('http').IncomingHttpHeaders
  hostname: string
  port: string
  query: import('querystring').ParsedUrlQuery
  // query: { [key: string]: string }
  auth: string | null
  hash: string | null
  host: string | null
  href: string
  path: string | null
  pathname: string | null
  protocol: string | null
  search: string | null
  slashes: boolean | null
}

export type MicrositePageContext = {
  cookieContext: CookieServerContext
  requestContext: RequestServerContext
  userSettings: UserSettings
  micrositeSettings: MicrositeSettings
  webToggles: WebTogglesWithOverrides
  req: ExpressLikeRequestContext
  optimizelyDatafile?: string
}

export type MicrositeApiContext = {
  cookieContext: CookieServerContext
  requestContext: RequestServerContext
  userSettings: UserSettings
  req: ExpressLikeRequestContext
}

export type MicrositeAppContext = Omit<MicrositePageContext, 'requestContext'> & {
  requestContext: RequestServerAppContext
  query: { [key: string]: string }
  optimizelyDatafile?: string
}

export type Site =
  | 'DustinSweden'
  | 'DustinHomeSweden'
  | 'DustinNorway'
  | 'DustinHomeNorway'
  | 'DustinDenmark'
  | 'DustinHomeDenmark'
  | 'DustinFinland'
  | 'DustinHomeFinland'
  | 'DustinNetherlands'
  | 'DustinHomeNetherlands'

export type SiteType = 'Business' | 'Consumer'

export type SourcingCompany = 'SE' | 'DK' | 'NO' | 'FI' | 'NL'

export type PaymentMethod = {
  id:
    | 'mc'
    | 'visa'
    | 'visadankort'
    | 'trustly'
    | 'ideal'
    | 'ebanking_FI'
    | 'klarna'
    | 'swish'
    | 'invoice'
    | 'mobilepay'
  type: 'creditcard' | 'bankdirect' | 'klarna' | 'invoice'
  displayName: string
}
export type DeliveryMethod = {
  id: 'postnord' | 'bring' | 'posti' | 'postnl'
  displayName: string
}

export const getSiteType = (site: Site): SiteType => {
  if (site.includes('Home')) {
    return 'Consumer'
  }

  return 'Business'
}

export const getDefaultShowInclVat = (site: Site) => {
  return getSiteType(site) === 'Consumer'
}

export const getSourcingCompany = (site: Site): SourcingCompany => {
  switch (site) {
    case 'DustinNorway':
    case 'DustinHomeNorway':
      return 'NO'
    case 'DustinDenmark':
    case 'DustinHomeDenmark':
      return 'DK'
    case 'DustinFinland':
    case 'DustinHomeFinland':
      return 'FI'
    case 'DustinNetherlands':
    case 'DustinHomeNetherlands':
      return 'NL'
    case 'DustinSweden':
    case 'DustinHomeSweden':
    default:
      return 'SE'
  }
}

export const getAvailableLanguages = (site: Site): Language[] => {
  switch (site) {
    case 'DustinNorway':
    case 'DustinHomeNorway':
      return ['Norwegian', 'English']
    case 'DustinDenmark':
    case 'DustinHomeDenmark':
      return ['Danish', 'English']
    case 'DustinFinland':
      return ['Finnish', 'Swedish', 'English']
    case 'DustinHomeFinland':
      return ['Finnish', 'Swedish', 'English']
    case 'DustinNetherlands':
    case 'DustinHomeNetherlands':
      return ['Dutch', 'English']
    case 'DustinSweden':
    case 'DustinHomeSweden':
    default:
      return ['Swedish', 'English']
  }
}

export const getSiteName = (site: Site): string => {
  switch (site) {
    case 'DustinSweden':
      return 'Dustin.se'
    case 'DustinHomeSweden':
      return 'Dustinhome.se'
    case 'DustinNorway':
      return 'Dustin.no'
    case 'DustinHomeNorway':
      return 'Dustinhome.no'
    case 'DustinDenmark':
      return 'Dustin.dk'
    case 'DustinHomeDenmark':
      return 'Dustinhome.dk'
    case 'DustinFinland':
      return 'Dustin.fi'
    case 'DustinHomeFinland':
      return 'Dustinhome.fi'
    case 'DustinNetherlands':
      return 'Dustin.nl'
    case 'DustinHomeNetherlands':
      return 'Dustinhome.nl'
    default:
      console.error(`Unknown site: ${site}`)
      return `${site}`
  }
}

export const getCountryCode = (sourcingCompany: SourcingCompany): string => {
  switch (sourcingCompany) {
    case 'SE':
      return 'SWE'
    case 'DK':
      return 'DNK'
    case 'FI':
      return 'FIN'
    case 'NL':
      return 'NLD'
    case 'NO':
      return 'NOR'
    default:
      console.error(`Unknown sourcing company: ${sourcingCompany}`)
      return `${sourcingCompany}`
  }
}

export const getThemeColor = (site: Site): string => {
  const siteType = getSiteType(site)
  return siteType === 'Consumer' ? '#f6f6f6' : '#2e2e2e'
}

export const getSiteSelectorUrls = (site: Site, environment: string): SiteSelectorUrls => {
  switch (environment.toLocaleLowerCase()) {
    case 'production':
      return getProdSiteSelectorUrls(site)
    case 'staging':
      return getStageSiteSelectorUrls(site)
    case 'test':
      return getTestSiteSelectorUrls(site)
    case 'local':
      return getLocalSiteSelectorUrls()
    default:
      return getLocalSiteSelectorUrls()
  }
}

const getProdSiteSelectorUrls = (site: Site) => {
  switch (site) {
    case 'DustinNorway':
    case 'DustinHomeNorway':
      return { business: 'https://www.dustin.no', consumer: 'https://www.dustinhome.no' }
    case 'DustinDenmark':
    case 'DustinHomeDenmark':
      return { business: 'https://www.dustin.dk', consumer: 'https://www.dustinhome.dk' }
    case 'DustinFinland':
    case 'DustinHomeFinland':
      return { business: 'https://www.dustin.fi', consumer: 'https://www.dustinhome.fi' }
    case 'DustinNetherlands':
    case 'DustinHomeNetherlands':
      return { business: 'https://www.dustin.nl', consumer: 'https://www.dustinhome.nl' }
    case 'DustinSweden':
    case 'DustinHomeSweden':
      return { business: 'https://www.dustin.se', consumer: 'https://www.dustinhome.se' }

    default:
      return { business: 'https://www.dustin.se', consumer: 'https://www.dustinhome.se' }
  }
}

const getStageSiteSelectorUrls = (site: Site) => {
  switch (site) {
    case 'DustinNorway':
    case 'DustinHomeNorway':
      return {
        business: 'https://stage.dustin.no.dustingroup.org',
        consumer: 'https://stage.dustinhome.no.dustingroup.org',
      }
    case 'DustinDenmark':
    case 'DustinHomeDenmark':
      return {
        business: 'https://stage.dustin.dk.dustingroup.org',
        consumer: 'https://stage.dustinhome.dk.dustingroup.org',
      }
    case 'DustinFinland':
    case 'DustinHomeFinland':
      return {
        business: 'https://stage.dustin.fi.dustingroup.org',
        consumer: 'https://stage.dustinhome.fi.dustingroup.org',
      }
    case 'DustinNetherlands':
    case 'DustinHomeNetherlands':
      return {
        business: 'https://stage.dustin.nl.dustingroup.org/',
        consumer: 'https://stage.dustinhome.nl.dustingroup.org/',
      }
    case 'DustinSweden':
    case 'DustinHomeSweden':
      return {
        business: 'https://stage.dustin.se.dustingroup.org',
        consumer: 'https://stage.dustinhome.se.dustingroup.org',
      }

    default:
      return {
        business: 'https://stage.dustin.se.dustingroup.org',
        consumer: 'https://stage.dustinhome.se.dustingroup.org',
      }
  }
}

const getTestSiteSelectorUrls = (site: Site) => {
  switch (site) {
    case 'DustinNorway':
    case 'DustinHomeNorway':
      return {
        business: 'https://test.dustin.no',
        consumer: 'https://test.dustinhome.no',
      }
    case 'DustinDenmark':
    case 'DustinHomeDenmark':
      return {
        business: 'https://test.dustin.dk',
        consumer: 'https://test.dustinhome.dk',
      }
    case 'DustinFinland':
    case 'DustinHomeFinland':
      return {
        business: 'https://test.dustin.fi',
        consumer: 'https://test.dustinhome.fi',
      }
    case 'DustinNetherlands':
    case 'DustinHomeNetherlands':
      return {
        business: 'https://test.dustin.nl',
        consumer: 'https://test.dustinhome.nl',
      }
    case 'DustinSweden':
    case 'DustinHomeSweden':
      return {
        business: 'https://test.dustin.se',
        consumer: 'https://test.dustinhome.se',
      }

    default:
      return {
        business: 'https://test.dustin.se',
        consumer: 'https://test.dustinhome.se',
      }
  }
}

const getLocalSiteSelectorUrls = () => {
  const localUrl = 'http://localhost:' + (process.env.PORT || '3000')
  return { business: localUrl, consumer: localUrl }
}

export const getSiteFromHostname = (hostname: string) => {
  switch (hostname) {
    case 'localsebusiness':
    case 'local.dustin.se':
    case 'test.dustin.se':
    case 'stage.dustin.se.dustingroup.org':
    case 'dustin.se':
      return 'DustinSweden'

    case 'localseconsumer':
    case 'local.dustinhome.se':
    case 'test.dustinhome.se':
    case 'stage.dustinhome.se.dustingroup.org':
    case 'dustinhome.se':
      return 'DustinHomeSweden'

    case 'localfibusiness':
    case 'local.dustin.fi':
    case 'test.dustin.fi':
    case 'stage.dustin.fi.dustingroup.org':
    case 'dustin.fi':
      return 'DustinFinland'

    case 'localficonsumer':
    case 'local.dustinhome.fi':
    case 'test.dustinhome.fi':
    case 'stage.dustinhome.fi.dustingroup.org':
    case 'dustinhome.fi':
      return 'DustinHomeFinland'

    case 'localnobusiness':
    case 'local.dustin.no':
    case 'test.dustin.no':
    case 'stage.dustin.no.dustingroup.org':
    case 'dustin.no':
      return 'DustinNorway'

    case 'localnoconsumer':
    case 'local.dustinhome.no':
    case 'test.dustinhome.no':
    case 'stage.dustinhome.no.dustingroup.org':
    case 'dustinhome.no':
      return 'DustinHomeNorway'

    case 'localdkbusiness':
    case 'local.dustin.dk':
    case 'test.dustin.dk':
    case 'stage.dustin.dk.dustingroup.org':
    case 'dustin.dk':
      return 'DustinDenmark'

    case 'localdkconsumer':
    case 'local.dustinhome.dk':
    case 'test.dustinhome.dk':
    case 'stage.dustinhome.dk.dustingroup.org':
    case 'dustinhome.dk':
      return 'DustinHomeDenmark'

    case 'localnlbusiness':
    case 'local.dustin.nl':
    case 'test.dustin.nl':
    case 'stage.dustin.com.dustingroup.org':
    case 'dustin.nl':
      return 'DustinNetherlands'

    case 'localnlconsumer':
    case 'local.dustinhome.nl':
    case 'test.dustinhome.nl':
    case 'stage.dustinhome.nl.dustingroup.org':
    case 'dustinhome.nl':
      return 'DustinHomeNetherlands'

    default:
      return 'DustinSweden'
  }
}

export const getCurrencyCode = (site: Site) => {
  switch (site) {
    case 'DustinNorway':
    case 'DustinHomeNorway':
      return 'NOK'
    case 'DustinDenmark':
    case 'DustinHomeDenmark':
      return 'DKK'
    case 'DustinFinland':
    case 'DustinHomeFinland':
    case 'DustinNetherlands':
    case 'DustinHomeNetherlands':
      return 'EUR'
    case 'DustinSweden':
    case 'DustinHomeSweden':
      return 'SEK'
  }
}

export const getPaymentMethods = (site: Site): PaymentMethod[] => {
  const mc: PaymentMethod = {
    id: 'mc',
    type: 'creditcard',
    displayName: 'Mastercard',
  }
  const visa: PaymentMethod = {
    id: 'visa',
    type: 'creditcard',
    displayName: 'Visa',
  }
  const trustly: PaymentMethod = {
    id: 'trustly',
    type: 'bankdirect',
    displayName: 'Trustly',
  }
  const swish: PaymentMethod = {
    id: 'swish',
    type: 'bankdirect',
    displayName: 'Swish',
  }
  const invoice: PaymentMethod = {
    id: 'invoice',
    type: 'invoice',
    displayName: 'Invoice',
  }
  const mobilepay: PaymentMethod = {
    id: 'mobilepay',
    type: 'bankdirect',
    displayName: 'Mobilepay',
  }

  switch (site) {
    case 'DustinNorway':
    case 'DustinSweden':
      return [mc, visa, trustly, invoice]
    case 'DustinDenmark':
      return [
        mc,
        visa,
        {
          id: 'visadankort',
          type: 'bankdirect',
          displayName: 'Visa/Dankort',
        },
        trustly,
        invoice,
      ]
    case 'DustinFinland':
      return [
        mc,
        visa,
        {
          id: 'ebanking_FI',
          type: 'bankdirect',
          displayName: 'Paytrail',
        },
        invoice,
      ]
    case 'DustinNetherlands':
      return [
        mc,
        visa,
        {
          id: 'ideal',
          type: 'bankdirect',
          displayName: 'iDeal',
        },
        invoice,
      ]
    case 'DustinHomeNorway':
      return [
        mc,
        visa,
        {
          id: 'klarna',
          type: 'klarna',
          displayName: 'Klarna',
        },
      ]
    case 'DustinHomeDenmark':
      return [
        mc,
        visa,
        {
          id: 'klarna',
          type: 'klarna',
          displayName: 'Klarna',
        },
        mobilepay,
      ]
    case 'DustinHomeFinland':
      return [
        mc,
        visa,
        {
          id: 'klarna',
          type: 'klarna',
          displayName: 'Klarna',
        },
      ]
    case 'DustinHomeNetherlands':
      return [
        {
          id: 'klarna',
          type: 'klarna',
          displayName: 'Klarna',
        },
        {
          id: 'ideal',
          type: 'bankdirect',
          displayName: 'iDeal',
        },
      ]
    case 'DustinHomeSweden':
      return [
        mc,
        visa,
        swish,
        {
          id: 'klarna',
          type: 'klarna',
          displayName: 'Klarna',
        },
      ]
  }
}

export const getDeliveryMethods = (site: Site): DeliveryMethod[] => {
  switch (site) {
    case 'DustinNorway':
    case 'DustinHomeNorway':
    case 'DustinDenmark':
    case 'DustinHomeDenmark':
      return [
        {
          id: 'bring',
          displayName: 'Bring',
        },
      ]
    case 'DustinFinland':
    case 'DustinHomeFinland':
      return [
        {
          id: 'posti',
          displayName: 'Posti',
        },
      ]
    case 'DustinNetherlands':
    case 'DustinHomeNetherlands':
      return [
        {
          id: 'postnl',
          displayName: 'PostNL',
        },
      ]
    case 'DustinSweden':
    case 'DustinHomeSweden':
      return [
        {
          id: 'postnord',
          displayName: 'Postnord',
        },
      ]
  }
}

export const getEnvironmentUrl = (baseUri: string, environment: string, isBrowserUri = false) => {
  if (isBrowserUri) {
    baseUri = baseUri.replace(/^http:\/\//i, '//')
  }

  switch (environment) {
    case 'local':
    case 'test':
      return `${baseUri}.test.cnt.dustintech.dev`
    case 'aws_local':
    case 'aws_test':
      return `${baseUri}.test.cnt.dustintech.dev`
    case 'staging':
    case 'aws_staging':
      return `${baseUri}.acc.cnt.dustintech.dev`
    case 'production':
    case 'aws_production':
      return `${baseUri}.cnt.dustintech.io`
    default:
      return `${baseUri}.test.cnt.dustintech.dev`
  }
}

export const getHeaderDomain = (
  path: string,
  isBusiness: boolean,
  sourcingCompany: SourcingCompany
) => {
  const isNL = sourcingCompany === 'NL'

  const product = ['/group', '/product', '/accessories', '/compare', '/brands']
  const solutions = [getSolutionsUrl(sourcingCompany)]
  const knowledgeBank = [getKnowledgeBankUrl(sourcingCompany)]
  const customerService = [getServiceUrl(sourcingCompany)]

  if (product.some(item => path.startsWith(item))) {
    return 'Product'
  }

  if (knowledgeBank.some(item => path.startsWith(item)) && isBusiness) {
    return 'Knowledge bank'
  }

  if (solutions.some(item => path.startsWith(item)) && isBusiness && !isNL) {
    return 'Solutions'
  }

  if (customerService.some(item => path.startsWith(item))) {
    return 'Customer service'
  }

  return ''
}

export const getSolutionsUrl = (sourcingCompany: SourcingCompany) => {
  switch (sourcingCompany) {
    case 'NO':
      return '/tjenester'
    case 'NL':
      return `/oplossingen`
    default:
      return `/solutions`
  }
}

export const getGuidesUrl = (sourcingCompany: SourcingCompany) => {
  switch (sourcingCompany) {
    case 'SE':
      return '/editorial/guider'
    case 'NO':
      return '/editorial/guider'
    case 'DK':
      return '/editorial/guides'
    case 'FI':
      return '/editorial/oppaat'
    case 'NL':
      return '/artikelen'
    default:
      return '/editorial'
  }
}

export const getKnowledgeBankUrl = (sourcingCompany: SourcingCompany) => {
  const notInSolutions = sourcingCompany === 'NL'
  const solutionsUrl = notInSolutions ? '' : getSolutionsUrl(sourcingCompany)

  let url: string

  switch (sourcingCompany) {
    case 'DK':
      url = `${solutionsUrl}/vidensbanken`
      break
    case 'NO':
      url = `${solutionsUrl}/kunnskapsbanken`
      break
    case 'FI':
      url = `${solutionsUrl}/tietopankki`
      break
    case 'NL':
      url = `${solutionsUrl}/kennisbank`
      break
    default:
      url = `${solutionsUrl}/kunskapsbanken`
  }

  return url
}

export const getServiceUrl = (sourcingCompany: SourcingCompany) => {
  let url

  switch (sourcingCompany) {
    case 'NL':
      url = `/klantenservice`
      break
    default:
      url = `/service`
  }

  return url
}

export const buildDerivedSiteContext = (context: RequestServerContext) => {
  const sourcingCompany = getSourcingCompany(context.site)
  const siteType = getSiteType(context.site)
  const availableLanguages = getAvailableLanguages(context.site)
  const isConsumer = siteType === 'Consumer'
  const isBusiness = siteType === 'Business'

  const siteSelectorUrls = getSiteSelectorUrls(context.site, context.serverEnv)
  const consumerSiteUrl = siteSelectorUrls.consumer
  const businessSiteUrl = siteSelectorUrls.business
  const siteUrl = context.siteUrl
  const currencyCode = getCurrencyCode(context.site)
  const isMobile = !!context.deviceType && MOBILE_TYPES.includes(context.deviceType)

  return {
    ...context,
    sourcingCompany,
    siteType,
    availableLanguages,
    isConsumer,
    isBusiness,
    consumerSiteUrl,
    businessSiteUrl,
    siteUrl,
    currencyCode,
    isMobile,
  }
}
